/* ============================================================================
   AMELIORER — subir la calidad de una foto sin inventar nada (F4)
   ----------------------------------------------------------------------------
   Franklin lo pidio asi, literalmente: « que mejore la calidad, sin deformar
   nada de nada », para usarlo ANTES de quitar el fondo y tambien suelto en
   fotos que se ven pixeladas.

   POR QUE ESTO NO ES UN MODELO GENERATIVO, Y POR QUE ESO ES LO CORRECTO AQUI.
   Un upscaler generativo (ESRGAN y familia) INVENTA pixeles: alucina pelo,
   textura de piel y letras que no estaban. En una foto para redes eso queda
   bien; en un producto que se IMPRIME y que el cliente recibe en casa, es
   exactamente lo que no se quiere — y es justo lo que Franklin excluyo con
   « sin inventar ni deformar ». Ademas cuesta 5-20 Mo de modelo y bastante
   tiempo en un telefono.

   Lo que se hace en su lugar es lo que un buen laboratorio de impresion hace:

     1. REMUESTREO LANCZOS-3. Es el filtro de referencia para ampliar: pesa los
        pixeles vecinos con una ventana sinc y conserva los bordes mucho mejor
        que el bilineal del navegador, que emborrona. No anade informacion que
        no estuviera; la reparte bien.
     2. MASCARA DE ENFOQUE (unsharp) suave sobre el resultado. Ampliar siempre
        ablanda; esto devuelve el filo sin crear halos, con un umbral que deja
        en paz las zonas planas para no realzar el ruido del sensor.

   Resultado honesto: una foto pixelada se ve claramente mejor impresa y el DPI
   efectivo sube de verdad, porque hay mas pixeles reales por centimetro. Lo
   que no hace —y no debe hacer— es resucitar detalle que la camara nunca
   capturo.
   ========================================================================== */

const AMELIORER = (() => {
'use strict';

/* Tope de salida. Mas alla de esto no aporta a 300 DPI en los tamanos que
   imprime EDISAMP (el mayor es la camiseta, 45 cm = 5315 px) y en un movil
   empieza a haber problemas de memoria. */
/* EN EL TELÉFONO, MÁS PEQUEÑO. Franklin, en su iPhone: « estaba al 80 % y
   luego me saltó automáticamente el error ». El 80 % es « Netteté »: enfocar
   una imagen de 4200 px de lado —13 megapíxeles, con varios búferes a la vez—
   supera lo que Safari deja a una pestaña, y la mata. En el móvil el techo es
   2800 px y 7 megapíxeles: a 35 cm de ancho siguen siendo unos 200 DPI, por
   encima del umbral de aviso (150). En el ordenador no cambia nada. */
const ES_MOVIL = (() => {
  try {
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
      || Math.min(innerWidth, innerHeight) < 820;
  } catch (e) { return false; }
})();
const MAX_COTE = ES_MOVIL ? 2800 : 4200;
const MAX_PIXELS = ES_MOVIL ? 7e6 : 18e6;

/* ------------------------------- Lanczos -------------------------------- */
const A = 3;   // lobulos; 3 es el equilibrio clasico nitidez/artefactos

function sinc(x) {
  if (x === 0) return 1;
  const p = Math.PI * x;
  return Math.sin(p) / p;
}
function lanczos(x) {
  x = Math.abs(x);
  return x >= A ? 0 : sinc(x) * sinc(x / A);
}

/* Pesos de una dimension, precalculados por pixel de DESTINO. Se hace una vez
   por eje y se reutiliza en todas las filas: hacerlo por pixel seria un orden
   de magnitud mas lento y en un movil se notaria. */
function poids(origen, destino) {
  const escala = origen / destino;
  const soporte = Math.max(1, escala) * A;
  const filas = [];
  for (let i = 0; i < destino; i++) {
    const centro = (i + 0.5) * escala - 0.5;
    const ini = Math.max(0, Math.ceil(centro - soporte));
    const fin = Math.min(origen - 1, Math.floor(centro + soporte));
    const w = [];
    let suma = 0;
    for (let j = ini; j <= fin; j++) {
      const v = lanczos((j - centro) / Math.max(1, escala));
      w.push(v); suma += v;
    }
    /* Normalizar es obligatorio: si los pesos no suman 1, la imagen cambia de
       brillo al ampliarla. */
    if (suma !== 0) for (let k = 0; k < w.length; k++) w[k] /= suma;
    filas.push({ ini, fin, w });
  }
  return filas;
}

function redimensionner(src, dw, dh) {
  const sw = src.width, sh = src.height;
  const ctxS = src.getContext('2d');
  const datos = ctxS.getImageData(0, 0, sw, sh).data;

  /* Paso 1: horizontal, a un buffer intermedio de dw x sh. Separar los dos
     ejes convierte un coste O(n^2) por pixel en O(n) dos veces. */
  const px = poids(sw, dw);
  const tmp = new Float32Array(dw * sh * 4);
  for (let y = 0; y < sh; y++) {
    for (let x = 0; x < dw; x++) {
      const p = px[x];
      let r = 0, g = 0, b = 0, a = 0;
      for (let j = p.ini, k = 0; j <= p.fin; j++, k++) {
        const i = (y * sw + j) * 4, w = p.w[k];
        /* Se premultiplica por alfa: sin esto, los pixeles transparentes
           arrastran su color y aparecen halos en los bordes recortados. */
        const al = datos[i+3] / 255;
        r += datos[i]   * al * w; g += datos[i+1] * al * w;
        b += datos[i+2] * al * w; a += datos[i+3] * w;
      }
      const o = (y * dw + x) * 4;
      tmp[o] = r; tmp[o+1] = g; tmp[o+2] = b; tmp[o+3] = a;
    }
  }

  /* Paso 2: vertical, al destino final. */
  const py = poids(sh, dh);
  const out = new Uint8ClampedArray(dw * dh * 4);
  for (let y = 0; y < dh; y++) {
    const p = py[y];
    for (let x = 0; x < dw; x++) {
      let r = 0, g = 0, b = 0, a = 0;
      for (let j = p.ini, k = 0; j <= p.fin; j++, k++) {
        const i = (j * dw + x) * 4, w = p.w[k];
        r += tmp[i] * w; g += tmp[i+1] * w; b += tmp[i+2] * w; a += tmp[i+3] * w;
      }
      const o = (y * dw + x) * 4;
      const al = a / 255;
      out[o]   = al > 0 ? r / al : 0;
      out[o+1] = al > 0 ? g / al : 0;
      out[o+2] = al > 0 ? b / al : 0;
      out[o+3] = a;
    }
  }

  const dst = document.createElement('canvas');
  dst.width = dw; dst.height = dh;
  dst.getContext('2d').putImageData(new ImageData(out, dw, dh), 0, 0);
  return dst;
}

/* --------------------------- mascara de enfoque -------------------------- */
/* Unsharp clasico: original + fuerza * (original - desenfocado). El UMBRAL es
   lo que separa un buen enfoque de uno que realza el ruido: solo se refuerza
   donde la diferencia local es apreciable, o sea en los BORDES, y se deja en
   paz un cielo o una pared lisa. */
function accentuer(cv, fuerza, umbral) {
  const w = cv.width, h = cv.height;
  const ctx = cv.getContext('2d');
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  const orig = new Uint8ClampedArray(d);

  /* Desenfoque 3x3 tipo gaussiano, suficiente para un unsharp fino. */
  const K = [1,2,1, 2,4,2, 1,2,1], KS = 16;
  const flou = new Uint8ClampedArray(d.length);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      for (let c = 0; c < 3; c++) {
        let s = 0, k = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++, k++) {
            const yy = Math.min(h-1, Math.max(0, y+dy));
            const xx = Math.min(w-1, Math.max(0, x+dx));
            s += orig[(yy*w+xx)*4 + c] * K[k];
          }
        }
        flou[(y*w+x)*4 + c] = s / KS;
      }
      flou[(y*w+x)*4 + 3] = orig[(y*w+x)*4 + 3];
    }
  }

  for (let i = 0; i < d.length; i += 4) {
    for (let c = 0; c < 3; c++) {
      const diff = orig[i+c] - flou[i+c];
      d[i+c] = Math.abs(diff) > umbral ? orig[i+c] + fuerza * diff : orig[i+c];
    }
  }
  ctx.putImageData(img, 0, 0);
  return cv;
}

/* --------------------------------- API ----------------------------------- */
/* ¿Merece la pena? Ampliar una foto que ya es grande no aporta nada y solo
   gasta memoria: se dice, en vez de fingir una mejora. */
function analyser(natW, natH, cmW) {
  const dpi = (cmW > 0) ? Math.round(natW / cmW * 2.54) : null;
  const cote = Math.max(natW, natH);
  return {
    dpi,
    dejaBon: cote >= MAX_COTE || (dpi !== null && dpi >= 300),
    facteurUtile: Math.max(1, Math.min(3, MAX_COTE / cote,
                            dpi ? Math.max(1, 300 / Math.max(dpi, 1)) : 2)),
  };
}

/* dataUrl -> dataUrl PNG mejorado. `facteur` por defecto lo decide analyser. */
async function ameliorer(dataUrl, facteur, surProgres) {
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i); i.onerror = rej;
    i.src = dataUrl;
  });

  const f = Math.max(1.2, Math.min(3, facteur || 2));
  let dw = Math.min(MAX_COTE, Math.round(img.naturalWidth * f));
  let dh = Math.round(img.naturalHeight * (dw / img.naturalWidth));
  /* El lado no basta: una foto vertical de 2800 × 3700 también es mucha
     memoria. Se limita el total de píxeles. */
  if (dh > MAX_COTE) { dw = Math.round(dw * MAX_COTE / dh); dh = MAX_COTE; }
  if (dw * dh > MAX_PIXELS) {
    const k = Math.sqrt(MAX_PIXELS / (dw * dh));
    dw = Math.round(dw * k); dh = Math.round(dh * k);
  }

  surProgres && surProgres(10, 'Analyse');
  const src = document.createElement('canvas');
  src.width = img.naturalWidth; src.height = img.naturalHeight;
  src.getContext('2d').drawImage(img, 0, 0);

  surProgres && surProgres(35, 'Agrandissement');
  /* Ceder el hilo antes de la parte pesada: sin esto la barra de progreso se
     pinta ya terminada y el cliente ve un salto, no un progreso. */
  await new Promise(r => setTimeout(r, 0));
  const grand = redimensionner(src, dw, dh);

  surProgres && surProgres(80, 'Netteté');
  await new Promise(r => setTimeout(r, 0));
  accentuer(grand, 0.55, 3);

  surProgres && surProgres(100, 'Terminé');
  return grand.toDataURL('image/png');
}

return { ameliorer, analyser, MAX_COTE };
})();
